import { arrayOf, bool, func, shape, string, number, node } from 'prop-types';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import React, { Component, Fragment } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PostBundle from 'components/PostBundle';
import LazyLoad from 'components/LazyLoad';
import { hasTermWithSlug } from 'util/terms';
import { BLOCK_SPOTIM_SLUG } from 'constants/tags';
import AmazonOneTag from 'components/AmazonOneTag';
import Skimlinks from 'components/Skimlinks';
import CoverMedia from 'components/CoverMedia';
import Loading from 'components/Loading';
import Figure from 'components/Figure';
import SlideGallery from 'components/SlideGallery';
import Trackonomics from 'components/Trackonomics';
import { HAS_COMMENTS, HAS_PRISM, HAS_RECIRC, SITE_NAME } from 'constants/site';
import Video from 'components/Video';
import withAutoLink from 'lib/withAutoLink';
import { setCurrentPost } from 'actions/post';
import { filterTermsBySlug } from 'util/terms';
import whitelist from '../../whitelist.json';

const Recirc = dynamic(() => import('components/Recirc'), {
  ssr: false
});

const SiteComments = dynamic(() => import('components/SiteComments'), {
  ssr: false
});

const MEDIA_MAP = {
  gallery: SlideGallery,
  image: Figure,
  video: Video
};

export class SitePostBundle extends Component {
  constructor(props) {
    super(props);

    this.state = {
      extraArticleClasses: [
        // Set noskim and noskimwords if this is NOT an affiliate post
        ...(props.isAffiliate === false ? ['noskim', 'noskimwords'] : [])
      ]
    };

    const {
      isPost,
      data: {
        resourceId,
        categories,
        tags,
        status,
        publishedAt,
        modifiedAt,
        updatedAt
      }
    } = props;

    const postMeta = {
      resourceId,
      categories,
      tags,
      publishedAt,
      modifiedAt,
      updatedAt,
      status,
      isPost
    };

    props.setCurrentPost(postMeta);

    this.setExtraArticleClasses = this.setExtraArticleClasses.bind(this);
  }

  componentWillUnmount() {
    // Unset post data
    this.props.setCurrentPost(undefined);
  }

  setExtraArticleClasses(extraArticleClasses = []) {
    this.setState({
      extraArticleClasses
    });
  }

  renderRecirc() {
    const { data, isSponsored } = this.props;
    const { canonical, categories, tags } = data;
    const [category] =
      !isSponsored && whitelist
        ? filterTermsBySlug(categories, whitelist.categories)
        : [];

    if (isSponsored || HAS_RECIRC === false) {
      return null;
    }

    return (
      <div className="pov_recirc">
        <div className="pov_recirc__more-in">
          <LazyLoad placeholder={<Loading height="300px" />} once>
            <Recirc
              name="post-page__more-in"
              design="nested"
              title={
                !category ? (
                  `More from ${SITE_NAME}`
                ) : (
                  <a
                    href={`/${category.slug}`}
                    dangerouslySetInnerHTML={{
                      __html: `More in ${category.name}`
                    }}
                  />
                )
              }
              canonical={canonical}
              categories={category ? [category] : undefined}
              tags={tags}
              limit={6}
            />
          </LazyLoad>
        </div>
        <div className="pov_recirc__popular pov_recirc__popular--sticky">
          <LazyLoad placeholder={<Loading height="300px" />} once>
            <Recirc
              name="right-rail__popular"
              design="thumbnail-serif"
              title="Trending"
              canonical={canonical}
              limit={4}
              useTrending
            />
          </LazyLoad>
        </div>
      </div>
    );
  }

  render() {
    const {
      data,
      isAffiliate,
      isSponsored,
      hideAds,
      hidePrism,
      additionalAffiliateText
    } = this.props;

    const {
      canonical,
      resourceId,
      categories,
      tags,
      coverMedia,
      ads,
      title
    } = data;

    const { extraArticleClasses } = this.state;
    const { setExtraArticleClasses } = this;

    // keep track of num of image and gallery items in each post bundle
    let imageItemIdx = 0;

    // provide an object to merge into customPropsMapper results.
    const itemPropMapperAdditions = (itemProps, type, variant, idx) => ({
      // Show title for videos
      ...(type === 'video' && {
        showTitle: true,
        setExtraArticleClasses
      }),

      // Don't lazy load the first image in the post if it is the first item
      ...(type === 'image' &&
        idx === 0 && {
          hasLazyImage: false
        }),

      // Hide image actions on posts tagged 'small-cool-experience-2020'
      ...((['image', 'gallery'].includes(type) &&
        hasTermWithSlug(tags, 'small-cool-experience-2020')) ||
        (hasTermWithSlug(tags, 'action-hide-image-toolbar') && {
          hideImageActions: true
        })),

      // track the number of images and galleries in this post and let the
      // bundle item know its index.
      ...(['image', 'gallery'].includes(type) && {
        // eslint-disable-next-line no-plusplus
        imageItemIdx: imageItemIdx++
      })
    });

    const recirc = this.renderRecirc();

    const coverMediaEl = coverMedia && (
      <CoverMedia
        {...coverMedia}
        hasPrism={HAS_PRISM && !hidePrism}
        components={MEDIA_MAP}
        post={{
          ads,
          canonical,
          title
        }}
      />
    );

    return (
      <Fragment>
        {isAffiliate && (
          <Fragment>
            {/* scope onetag to the current post bundle only */}
            <AmazonOneTag />
            <Skimlinks />
            <Trackonomics />
          </Fragment>
        )}

        <PostBundle
          {...this.props}
          extraArticleClasses={extraArticleClasses}
          itemPropMapperAdditions={itemPropMapperAdditions}
          coverMedia={coverMediaEl}
          recirc={recirc}
          comments={
            HAS_COMMENTS ? (
              <LazyLoad height={200}>
                <SiteComments
                  resourceId={resourceId}
                  canonical={canonical}
                  hideAds={hideAds || isSponsored}
                  ignoreHostUrl={hasTermWithSlug(tags, BLOCK_SPOTIM_SLUG)}
                />
              </LazyLoad>
            ) : null
          }
          additionalAffiliateText={additionalAffiliateText}
        />
      </Fragment>
    );
  }
}

SitePostBundle.propTypes = {
  setCurrentPost: func.isRequired,
  hidePrism: bool.isRequired,
  data: shape({
    resourceId: string.isRequired,
    canonical: string.isRequired,
    categories: arrayOf(
      shape({
        name: string,
        slug: string
      })
    ),
    tags: arrayOf(
      shape({
        name: string,
        slug: string
      })
    ),
    status: string.isRequired,
    publishedAt: number.isRequired,
    modifiedAt: number.isRequired,
    updatedAt: number,
    coverMedia: shape({
      // We don't need the full shape because PostBundler sets the
      // `post` part for the CoverMedia component
      type: CoverMedia.propTypes.type,
      data: CoverMedia.propTypes.data
    })
  }).isRequired,
  hideAds: bool.isRequired,
  hideComments: bool.isRequired,
  hideAccountActions: bool.isRequired,
  isAffiliate: bool.isRequired,
  isDraft: bool.isRequired,
  isPost: bool.isRequired,
  isNoIndex: bool.isRequired,
  isSponsored: bool.isRequired,
  additionalAffiliateText: node
};

SitePostBundle.defaultProps = {
  additionalAffiliateText: undefined
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      setCurrentPost
    },
    dispatch
  );

export default connect(
  null,
  mapDispatchToProps
)(withAutoLink(SitePostBundle));
